import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { Toaster } from "@/components/ui/sonner";
import GoogleAnalytics from "@/components/GoogleAnalytics";

// Layout
import Layout from "@/components/Layout";

// Critical Pages - Loaded immediately for homepage and primary navigation
import HomePage from "@/pages/HomePage";
import LotteryPage from "@/pages/LotteryPage";
import PredictionsPage from "@/pages/PredictionsPage";
import LotteriesPage from "@/pages/LotteriesPage";
import AboutPage from "@/pages/AboutPage";

// LOTTERY-SPECIFIC PAGES - Critical for SEO, loaded immediately
import EuroMillionsPage from "@/pages/EuroMillionsPage";
import EuroJackpotPage from "@/pages/EuroJackpotPage";
import SwissLottoPage from "@/pages/SwissLottoPage";
import SuperEnalottoPage from "@/pages/SuperEnalottoPage";
import LaPrimitivaPage from "@/pages/LaPrimitivaPage";
import EuroDreamsPage from "@/pages/EuroDreamsPage";
import MegaSenaPage from "@/pages/MegaSenaPage";
import MegaMillionsPage from "@/pages/MegaMillionsPage";
import PowerballPage from "@/pages/PowerballPage";
import AustraliaPowerballPage from "@/pages/AustraliaPowerballPage";
import UKLottoPage from "@/pages/UKLottoPage";
import GermanLottoPage from "@/pages/GermanLottoPage";
import IrishLottoPage from "@/pages/IrishLottoPage";
import AustralianLottoPage from "@/pages/AustralianLottoPage";

// ============================================
// NON-CRITICAL PAGES - Code-split with lazy loading
// These are NOT part of the initial homepage bundle
// ============================================

// Statistics page - heavy component, rarely visited from homepage
const StatisticsPage = lazy(() => import("@/pages/StatisticsPage"));

// Admin pages - only visited by admins
const AdminToolsPage = lazy(() => import("@/pages/AdminToolsPage"));
const AdminLoginPage = lazy(() => import("@/pages/AdminLoginPage"));
const CSVImporterPage = lazy(() => import("@/pages/CSVImporterPage"));
const CSVDebuggerPage = lazy(() => import("@/pages/CSVDebuggerPage"));

// Legal/Info pages - rarely visited
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const DisclaimerPage = lazy(() => import("@/pages/DisclaimerPage"));
const CookiePolicyPage = lazy(() => import("@/pages/CookiePolicyPage"));

// Secondary pages
const LawOfAttractionPage = lazy(() => import("@/pages/LawOfAttractionPage"));
const PrayerToUniversePage = lazy(() => import("@/pages/PrayerToUniversePage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const ApiDocsPage = lazy(() => import("@/pages/ApiDocsPage"));
const DonatePage = lazy(() => import("@/pages/DonatePage"));
const DonateThankYouPage = lazy(() => import("@/pages/DonateThankYouPage"));
const MethodologyPage = lazy(() => import("@/pages/MethodologyPage"));
const ResearchPage = lazy(() => import("@/pages/ResearchPage"));
const ArticlePage = lazy(() => import("@/pages/ArticlePage"));
const AdminContentPage = lazy(() => import("@/pages/AdminContentPage"));
const AdminPipelineMonitorPage = lazy(() => import("@/pages/AdminPipelineMonitorPage"));
const ReportPage = lazy(() => import("@/pages/ReportPage"));

// SEO CONTENT PAGES - Large component, code-split
// Each page is lazy-loaded separately for optimal chunk splitting
const LazyLotteryResultsPage = lazy(() => 
  import("@/pages/LotterySEOPages").then(module => ({ default: module.default.LotteryResultsPage }))
);
const LazyLotteryStatisticsPage = lazy(() => 
  import("@/pages/LotterySEOPages").then(module => ({ default: module.default.LotteryStatisticsPage }))
);
const LazyLotteryHotColdPage = lazy(() => 
  import("@/pages/LotterySEOPages").then(module => ({ default: module.default.LotteryHotColdPage }))
);
const LazyLotteryFrequencyPage = lazy(() => 
  import("@/pages/LotterySEOPages").then(module => ({ default: module.default.LotteryFrequencyPage }))
);
const LazyLotteryPredictionsPage = lazy(() => 
  import("@/pages/LotterySEOPages").then(module => ({ default: module.default.LotteryPredictionsPage }))
);

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <GoogleAnalytics />
        <LanguageProvider>
          <Routes>

            {/* ROOT PATH - Renders all pages with default lang=en */}
            <Route path="/" element={<Layout isRoot={true} />}>
              <Route index element={<HomePage />} />
              
              {/* All pages accessible from root without /en prefix */}
              <Route path="lotteries" element={<LotteriesPage />} />
              <Route path="statistical-projections" element={<PredictionsPage />} />
              <Route path="predictions" element={<Navigate to="/statistical-projections" replace />} />
              <Route path="statistics" element={<Suspense fallback={null}><StatisticsPage /></Suspense>} />
              <Route path="law-of-attraction" element={<Navigate to="/culture/law-of-attraction" replace />} />
              <Route path="culture/law-of-attraction" element={<Suspense fallback={null}><LawOfAttractionPage /></Suspense>} />
              <Route path="prayer-to-universe" element={<Suspense fallback={null}><PrayerToUniversePage /></Suspense>} />
              <Route path="about" element={<AboutPage />} />
              <Route path="donate" element={<Suspense fallback={null}><DonatePage /></Suspense>} />
              <Route path="donate/thank-you" element={<Suspense fallback={null}><DonateThankYouPage /></Suspense>} />
              <Route path="privacy" element={<Suspense fallback={null}><PrivacyPage /></Suspense>} />
              <Route path="privacy-policy" element={<Suspense fallback={null}><PrivacyPolicyPage /></Suspense>} />
              <Route path="terms" element={<Suspense fallback={null}><TermsPage /></Suspense>} />
              <Route path="disclaimer" element={<Suspense fallback={null}><DisclaimerPage /></Suspense>} />
              <Route path="cookie-policy" element={<Suspense fallback={null}><CookiePolicyPage /></Suspense>} />
              <Route path="blog" element={<Navigate to="/research" replace />} />
              <Route path="contact" element={<Suspense fallback={null}><ContactPage /></Suspense>} />
              <Route path="methodology" element={<Suspense fallback={null}><MethodologyPage /></Suspense>} />
              <Route path="api-docs" element={<Suspense fallback={null}><ApiDocsPage /></Suspense>} />
              <Route path="admin-csv-tools-2025" element={<Suspense fallback={null}><CSVImporterPage /></Suspense>} />
              <Route path="csv-debugger" element={<Suspense fallback={null}><CSVDebuggerPage /></Suspense>} />
              <Route path="admin/login" element={<Suspense fallback={null}><AdminLoginPage /></Suspense>} />
              <Route path="admin/tools" element={<Suspense fallback={null}><AdminToolsPage /></Suspense>} />
              <Route path="admin/content" element={<Suspense fallback={null}><AdminContentPage /></Suspense>} />
              <Route path="admin/pipeline-monitor" element={<Suspense fallback={null}><AdminPipelineMonitorPage /></Suspense>} />
              
              {/* Research & Articles */}
              <Route path="research" element={<Suspense fallback={null}><ResearchPage /></Suspense>} />
              <Route path="articles/:slug" element={<Suspense fallback={null}><ArticlePage /></Suspense>} />
              <Route path="intelligence-reports/:yearWeek" element={<Suspense fallback={null}><ReportPage /></Suspense>} />
              
              {/* Lottery pages from root */}
              <Route path="lottery/euromillions" element={<EuroMillionsPage />} />
              <Route path="lottery/euromillion" element={<EuroMillionsPage />} />
              <Route path="lottery/euromillons" element={<EuroMillionsPage />} />
              <Route path="lottery/eurojackpot" element={<EuroJackpotPage />} />
              <Route path="lottery/euro-jackpot" element={<EuroJackpotPage />} />
              <Route path="lottery/swisslotto" element={<SwissLottoPage />} />
              <Route path="lottery/superenalotto" element={<SuperEnalottoPage />} />
              <Route path="lottery/laprimitiva" element={<LaPrimitivaPage />} />
              <Route path="lottery/eurodreams" element={<EuroDreamsPage />} />
              <Route path="lottery/megasena" element={<MegaSenaPage />} />
              <Route path="lottery/mega-sena" element={<MegaSenaPage />} />
              <Route path="lottery/megamillions" element={<MegaMillionsPage />} />
              <Route path="lottery/megamillion" element={<MegaMillionsPage />} />
              <Route path="lottery/mega-millions" element={<MegaMillionsPage />} />
              <Route path="lottery/powerball" element={<PowerballPage />} />
              <Route path="lottery/power-ball" element={<PowerballPage />} />
              <Route path="lottery/australia-powerball" element={<AustraliaPowerballPage />} />
              <Route path="lottery/uk-lotto" element={<UKLottoPage />} />
              <Route path="lottery/uklotto" element={<UKLottoPage />} />
              <Route path="lottery/german-lotto" element={<GermanLottoPage />} />
              <Route path="lottery/germanlotto" element={<GermanLottoPage />} />
              <Route path="lottery/6aus49" element={<GermanLottoPage />} />
              <Route path="lottery/irish-lotto" element={<IrishLottoPage />} />
              <Route path="lottery/irishlotto" element={<IrishLottoPage />} />
              
              {/* Australian Lotto (Saturday Lotto) */}
              <Route path="lottery/australian-lotto" element={<AustralianLottoPage />} />
              <Route path="lottery/australianlotto" element={<AustralianLottoPage />} />
              <Route path="lottery/saturday-lotto" element={<AustralianLottoPage />} />
              <Route path="lottery/saturdaylotto" element={<AustralianLottoPage />} />
              
              {/* SEO pages from root - Lazy loaded */}
              <Route path="lottery/:lotteryId/results" element={<Suspense fallback={null}><LazyLotteryResultsPage /></Suspense>} />
              <Route path="lottery/:lotteryId/statistics" element={<Suspense fallback={null}><LazyLotteryStatisticsPage /></Suspense>} />
              <Route path="lottery/:lotteryId/hot-cold" element={<Suspense fallback={null}><LazyLotteryHotColdPage /></Suspense>} />
              <Route path="lottery/:lotteryId/frequency" element={<Suspense fallback={null}><LazyLotteryFrequencyPage /></Suspense>} />
              <Route path="lottery/:lotteryId/predictions" element={<Suspense fallback={null}><LazyLotteryPredictionsPage /></Suspense>} />
              
              {/* Generic lottery fallback */}
              <Route path="lottery/:lotteryId" element={<LotteryPage />} />
            </Route>

            {/* All language-prefixed routes */}
            <Route path="/:lang" element={<Layout />}>

              {/* HOME */}
              <Route index element={<HomePage />} />

              {/* LOTTERIES LIST */}
              <Route path="lotteries" element={<LotteriesPage />} />

              {/* ------------------------------ */}
              {/* SPECIFIC LOTTERY PAGES (PRIORITY) */}
              {/* ------------------------------ */}

              {/* EuroMillions */}
              <Route path="lottery/euromillions" element={<EuroMillionsPage />} />
              <Route path="lottery/euromillion" element={<EuroMillionsPage />} />
              <Route path="lottery/euromillons" element={<EuroMillionsPage />} />

              {/* EuroJackpot */}
              <Route path="lottery/eurojackpot" element={<EuroJackpotPage />} />
              <Route path="lottery/euro-jackpot" element={<EuroJackpotPage />} />

              {/* Swiss Lotto */}
              <Route path="lottery/swisslotto" element={<SwissLottoPage />} />

              {/* SuperEnalotto */}
              <Route path="lottery/superenalotto" element={<SuperEnalottoPage />} />

              {/* La Primitiva */}
              <Route path="lottery/laprimitiva" element={<LaPrimitivaPage />} />

              {/* EuroDreams */}
              <Route path="lottery/eurodreams" element={<EuroDreamsPage />} />

              {/* Mega-Sena */}
              <Route path="lottery/megasena" element={<MegaSenaPage />} />
              <Route path="lottery/mega-sena" element={<MegaSenaPage />} />

              {/* Mega Millions */}
              <Route path="lottery/megamillions" element={<MegaMillionsPage />} />
              <Route path="lottery/megamillion" element={<MegaMillionsPage />} />
              <Route path="lottery/mega-millions" element={<MegaMillionsPage />} />

              {/* Powerball */}
              <Route path="lottery/powerball" element={<PowerballPage />} />
              <Route path="lottery/power-ball" element={<PowerballPage />} />

              {/* UK Lotto */}
              <Route path="lottery/uk-lotto" element={<UKLottoPage />} />
              <Route path="lottery/uklotto" element={<UKLottoPage />} />

              {/* German Lotto 6aus49 */}
              <Route path="lottery/german-lotto" element={<GermanLottoPage />} />
              <Route path="lottery/germanlotto" element={<GermanLottoPage />} />
              <Route path="lottery/6aus49" element={<GermanLottoPage />} />

              {/* Irish Lotto */}
              <Route path="lottery/irish-lotto" element={<IrishLottoPage />} />
              <Route path="lottery/irishlotto" element={<IrishLottoPage />} />

              {/* Australian Lotto (Saturday Lotto) */}
              <Route path="lottery/australian-lotto" element={<AustralianLottoPage />} />
              <Route path="lottery/australianlotto" element={<AustralianLottoPage />} />
              <Route path="lottery/saturday-lotto" element={<AustralianLottoPage />} />
              <Route path="lottery/saturdaylotto" element={<AustralianLottoPage />} />

              {/* ------------------------------ */}
              {/* SEO CONTENT PAGES (SUBPAGES FOR EACH LOTTERY) - Lazy loaded */}
              {/* ------------------------------ */}
              <Route path="lottery/:lotteryId/results" element={<Suspense fallback={null}><LazyLotteryResultsPage /></Suspense>} />
              <Route path="lottery/:lotteryId/statistics" element={<Suspense fallback={null}><LazyLotteryStatisticsPage /></Suspense>} />
              <Route path="lottery/:lotteryId/hot-cold" element={<Suspense fallback={null}><LazyLotteryHotColdPage /></Suspense>} />
              <Route path="lottery/:lotteryId/frequency" element={<Suspense fallback={null}><LazyLotteryFrequencyPage /></Suspense>} />
              <Route path="lottery/:lotteryId/predictions" element={<Suspense fallback={null}><LazyLotteryPredictionsPage /></Suspense>} />

              {/* ------------------------------ */}
              {/* GENERIC LOTTERY FALLBACK (MUST BE LAST) */}
              {/* ------------------------------ */}
              <Route path="lottery/:lotteryId" element={<LotteryPage />} />

              {/* OTHER PAGES - Lazy loaded */}
              <Route path="statistical-projections" element={<PredictionsPage />} />
              <Route path="predictions" element={<Navigate to="/statistical-projections" replace />} />
              <Route path="law-of-attraction" element={<Navigate to="/culture/law-of-attraction" replace />} />
              <Route path="culture/law-of-attraction" element={<Suspense fallback={null}><LawOfAttractionPage /></Suspense>} />
              <Route path="prayer-to-universe" element={<Suspense fallback={null}><PrayerToUniversePage /></Suspense>} />
              <Route path="statistics" element={<Suspense fallback={null}><StatisticsPage /></Suspense>} />
              
              {/* HIDDEN ADMIN ROUTES - Lazy loaded */}
              <Route path="admin-csv-tools-2025" element={<Suspense fallback={null}><CSVImporterPage /></Suspense>} />
              <Route path="csv-debugger" element={<Suspense fallback={null}><CSVDebuggerPage /></Suspense>} />
              <Route path="admin/login" element={<Suspense fallback={null}><AdminLoginPage /></Suspense>} />
              <Route path="admin/tools" element={<Suspense fallback={null}><AdminToolsPage /></Suspense>} />
              <Route path="about" element={<AboutPage />} />
              <Route path="donate" element={<Suspense fallback={null}><DonatePage /></Suspense>} />
              <Route path="donate/thank-you" element={<Suspense fallback={null}><DonateThankYouPage /></Suspense>} />
              <Route path="privacy" element={<Suspense fallback={null}><PrivacyPage /></Suspense>} />
              <Route path="privacy-policy" element={<Suspense fallback={null}><PrivacyPolicyPage /></Suspense>} />
              <Route path="terms" element={<Suspense fallback={null}><TermsPage /></Suspense>} />
              <Route path="disclaimer" element={<Suspense fallback={null}><DisclaimerPage /></Suspense>} />
              <Route path="cookie-policy" element={<Suspense fallback={null}><CookiePolicyPage /></Suspense>} />
              <Route path="blog" element={<Navigate to="/research" replace />} />
              <Route path="contact" element={<Suspense fallback={null}><ContactPage /></Suspense>} />
              <Route path="methodology" element={<Suspense fallback={null}><MethodologyPage /></Suspense>} />
              <Route path="api-docs" element={<Suspense fallback={null}><ApiDocsPage /></Suspense>} />
              <Route path="admin/content" element={<Suspense fallback={null}><AdminContentPage /></Suspense>} />
              <Route path="admin/pipeline-monitor" element={<Suspense fallback={null}><AdminPipelineMonitorPage /></Suspense>} />
              <Route path="research" element={<Suspense fallback={null}><ResearchPage /></Suspense>} />
              <Route path="articles/:slug" element={<Suspense fallback={null}><ArticlePage /></Suspense>} />
              <Route path="intelligence-reports/:yearWeek" element={<Suspense fallback={null}><ReportPage /></Suspense>} />

            </Route>

            {/* Catch-all - redirect to root */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          <Toaster position="bottom-right" />

        </LanguageProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
